import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

/**
 * Full-screen photo preview for medical-record attachments.
 *
 * • Renders the high-resolution `data_url` on a dark overlay.
 * • Left / right arrows (and swipe buttons) walk through the
 *   siblings supplied via `photos` so a practitioner can compare
 *   Face Chart → Photo → Informed Consent without closing the
 *   viewer.
 * • Escape / backdrop click closes.
 *
 * Props:
 *   open       — boolean
 *   onClose    — fn()
 *   photos     — [{ id?, kind, data_url, mime?, size? }]
 *   startIndex — number
 */
export default function MedicalPhotoLightbox({ open, onClose, photos = [], startIndex = 0 }) {
  const [idx, setIdx] = useState(startIndex);

  useEffect(() => { setIdx(startIndex); }, [startIndex, open]);

  const total = photos.length;
  const clamp = useCallback((n) => (total ? (n + total) % total : 0), [total]);
  const prev = useCallback(() => setIdx((i) => clamp(i - 1)), [clamp]);
  const next = useCallback(() => setIdx((i) => clamp(i + 1)), [clamp]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while the lightbox is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, prev, next]);

  if (!open || !total) return null;
  const p = photos[idx] || photos[0];
  const label = (p?.kind || "").replace(/_/g, " ");

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
      onClick={onClose}
      data-testid="medical-photo-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-3 text-white text-sm">
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-wider text-[11px] opacity-80">{label || "Photo"}</span>
          {total > 1 && (
            <span className="opacity-70 tabular-nums text-[11px]">{idx + 1} / {total}</span>
          )}
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <a
            href={p?.data_url}
            download={`${(p?.kind || "photo")}.jpg`}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Download photo"
            data-testid="medical-photo-lightbox-download"
          >
            <Download className="w-5 h-5" />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close preview"
            data-testid="medical-photo-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Prev / Next controls */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 sm:left-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Previous photo"
            data-testid="medical-photo-lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 sm:right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Next photo"
            data-testid="medical-photo-lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Image */}
      <img
        src={p?.data_url}
        alt={label || "Medical attachment"}
        className="max-w-[92vw] max-h-[86vh] object-contain rounded-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="medical-photo-lightbox-image"
      />
    </div>
  );
}
